<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>

          <div class="bh-panel">
            <!-- Encabezado rojo con logo -->
            <div class="bh-header">
              <span class="bh-badge">
                <img class="bh-badge-img" src="/static/img/logo_chico.png" alt="Somos Teatro" />
              </span>
              <span class="bh-header-title">
                HISTORIAL DE NEGOCIOS<template v-if="selectedPlay"> / {{ selectedPlay.title }}</template>
              </span>
            </div>

            <div class="bh-body">
              <div v-if="loading" class="d-flex justify-center py-10">
                <v-progress-circular indeterminate color="primary" size="48" />
              </div>

              <!-- LISTA: obras agrupadas por año de exhibición -->
              <template v-else-if="!selectedPlay">
                <div v-if="years.length === 0" class="bh-empty">
                  Aún no hay obras con funciones registradas.
                </div>

                <div v-for="group in years" :key="group.year" class="bh-year-block">
                  <h3 class="bh-year">{{ group.year }}</h3>
                  <div class="bh-cards">
                    <button
                      v-for="play in group.plays"
                      :key="play.id"
                      type="button"
                      class="bh-card"
                      @click="openPlay(play)"
                    >
                      <span class="bh-card-title">{{ play.title }}</span>
                      <span class="bh-card-range">{{ playRange(play) }}</span>
                    </button>
                  </div>
                </div>
              </template>

              <!-- DETALLE: todas las funciones de la obra -->
              <template v-else>
                <button type="button" class="bh-back" @click="closePlay">
                  <v-icon size="16">mdi-arrow-left</v-icon>
                  <span>VOLVER</span>
                </button>

                <div class="bh-detail-head">
                  <h2 class="bh-detail-title">{{ selectedPlay.title }}</h2>
                  <div class="bh-detail-years">{{ playYearSpan(selectedPlay) }}</div>
                </div>

                <div class="bh-table-scroll">
                  <table class="bh-table">
                    <thead>
                      <tr>
                        <th>FECHA</th>
                        <th>CLIENTE</th>
                        <th>CIUDAD</th>
                        <th>ESTADO</th>
                        <th>ÚLTIMO HITO</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="fn in selectedPlay.functions" :key="fn.id">
                        <td>{{ formatDate(fn.displayDate) }}</td>
                        <td class="bh-cell-strong">{{ fn.name || "—" }}</td>
                        <td>{{ (fn.client && fn.client.company && fn.client.company.city) || "—" }}</td>
                        <td>
                          <span class="bh-status" :class="estadoClass(fn.status)">
                            {{ estado(fn.status) }}
                          </span>
                        </td>
                        <td>{{ hito(fn.status) }}</td>
                        <td class="bh-cell-actions">
                          <a class="bh-docs-btn" :href="`/documento/${fn.id}/`">DOCUMENTOS</a>
                        </td>
                      </tr>
                      <tr v-if="selectedPlay.functions.length === 0">
                        <td colspan="6" class="bh-empty">Esta obra no tiene funciones registradas.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import { fetchBusinessHistory } from "@/services/teatroApi";

// Estado resumido mostrado en la tabla. El negocio se da por cerrado recién
// cuando la administración le pagó a la compañía de teatro.
const FINALIZADA_STATUSES = ["company_paid"];
const CANCELADA_STATUSES = ["cancelled"];

// Último hito alcanzado por la reserva (status -> etiqueta corta).
const HITO_LABELS = {
  pending: "SOLICITUD DE FECHA",
  pending_availability: "DISPONIBILIDAD DE FECHA",
  date_requested: "SOLICITUD DE FECHA",
  date_confirmed: "FECHA CONFIRMADA",
  contract_pending: "FIRMA DE CONTRATOS",
  deposit_pending: "FACTURA DE RESERVA",
  deposit_paid: "PAGO RESERVA 20%",
  balance_invoice_pending: "FACTURA DE SALDO",
  balance_pending: "PAGO SALDO PENDIENTE",
  show_completed: "FUNCIÓN REALIZADA",
  balance_paid: "PAGO SALDO 80%",
  company_invoice_pending: "FACTURA CÍA. TEATRO",
  company_payment_pending: "PAGO A LA CÍA. TEATRO",
  company_paid: "PAGO CÍA. REALIZADO",
  cancelled: "CANCELADA",
};

function yearOf(iso) {
  if (!iso) return null;
  const y = Number(String(iso).slice(0, 4));
  return Number.isFinite(y) && y > 0 ? y : null;
}

export default {
  name: "BusinessHistory",
  data() {
    return {
      reservations: [],
      loading: true,
      error: "",
      selectedPlay: null,
    };
  },
  computed: {
    // Obras (con sus funciones) agrupadas por año de inicio de temporada.
    years() {
      // 1) Agrupar las reservas por obra.
      const playsById = {};
      for (const r of this.reservations) {
        const p = r.play;
        if (!p || !p.id) continue;
        if (!playsById[p.id]) {
          playsById[p.id] = {
            id: p.id,
            title: p.title,
            startDate: p.startDate,
            endDate: p.endDate,
            functions: [],
          };
        }
        playsById[p.id].functions.push(r);
      }

      // 2) Ordenar las funciones de cada obra por fecha (más reciente primero).
      const plays = Object.values(playsById);
      for (const play of plays) {
        play.functions.sort((a, b) =>
          String(b.displayDate || "").localeCompare(String(a.displayDate || ""))
        );
      }

      // 3) Agrupar las obras por año de inicio de temporada (con respaldo a la
      //    fecha de función más antigua si la obra no tiene temporada).
      const byYear = {};
      for (const play of plays) {
        let year = yearOf(play.startDate);
        if (!year) {
          const fnYears = play.functions
            .map((f) => yearOf(f.displayDate))
            .filter(Boolean);
          year = fnYears.length ? Math.min(...fnYears) : null;
        }
        const key = year || "Sin fecha";
        (byYear[key] = byYear[key] || []).push(play);
      }

      // 4) Ordenar años descendente (más reciente primero; los sin fecha al
      //    final) y las obras por título.
      return Object.keys(byYear)
        .sort((a, b) => {
          if (a === "Sin fecha") return 1;
          if (b === "Sin fecha") return -1;
          return Number(b) - Number(a);
        })
        .map((year) => ({
          year,
          plays: byYear[year].sort((a, b) => a.title.localeCompare(b.title)),
        }));
    },
  },
  async created() {
    try {
      await this.$store.dispatch("fetchIsAuthenticated");
    } catch (e) {
      console.error(e);
    }
    await this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = "";
      try {
        this.reservations = await fetchBusinessHistory();
      } catch (e) {
        this.error = "No se pudo cargar el historial de negocios.";
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    openPlay(play) {
      this.selectedPlay = play;
    },
    closePlay() {
      this.selectedPlay = null;
    },
    formatDate(iso) {
      if (!iso) return "—";
      const [y, m, d] = String(iso).slice(0, 10).split("-");
      return y && m && d ? `${d}/${m}/${y}` : "—";
    },
    // Rango de temporada de la obra (para la caluga).
    playRange(play) {
      const s = this.formatDate(play.startDate);
      const e = this.formatDate(play.endDate);
      if (s !== "—" && e !== "—") return `${s} - ${e}`;
      return s !== "—" ? s : e;
    },
    // Rango de años que abarca la obra (para el encabezado del detalle).
    playYearSpan(play) {
      const yrs = [
        yearOf(play.startDate),
        yearOf(play.endDate),
        ...play.functions.map((f) => yearOf(f.displayDate)),
      ].filter(Boolean);
      if (!yrs.length) return "";
      const min = Math.min(...yrs);
      const max = Math.max(...yrs);
      return min === max ? `${min}` : `${min} - ${max}`;
    },
    estado(status) {
      const s = String(status || "").toLowerCase();
      if (CANCELADA_STATUSES.includes(s)) return "CANCELADA";
      if (FINALIZADA_STATUSES.includes(s)) return "FINALIZADA";
      return "ACTIVA";
    },
    estadoClass(status) {
      const s = String(status || "").toLowerCase();
      if (CANCELADA_STATUSES.includes(s)) return "bh-status-cancelled";
      if (FINALIZADA_STATUSES.includes(s)) return "bh-status-done";
      return "bh-status-active";
    },
    hito(status) {
      const s = String(status || "").toLowerCase();
      return HITO_LABELS[s] || "—";
    },
  },
};
</script>

<style scoped>
.bh-panel {
  max-width: 1080px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

/* --- Encabezado rojo con logo --- */
.bh-header {
  display: flex;
  align-items: center;
  gap: 18px;
  height: 78px;
  padding: 0 26px;
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
}

.bh-badge {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #0a0a0a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: 0 0 auto;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.45);
}

.bh-badge-img {
  width: 74%;
  height: 74%;
  object-fit: contain;
}

.bh-header-title {
  font-family: var(--font-family-heading);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1.4px;
}

.bh-body {
  padding: 26px 32px 34px;
  min-height: 320px;
}

/* --- Lista por año --- */
.bh-year-block {
  margin-bottom: 26px;
}

.bh-year {
  font-family: var(--font-family-heading);
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 0.6px;
  color: var(--color-theater-text);
  margin: 0 0 14px;
}

.bh-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.bh-card {
  width: 150px;
  min-height: 96px;
  background: #fff;
  border: none;
  border-radius: 12px;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  text-align: center;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.bh-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.45);
}

.bh-card-title {
  color: #c8102e;
  font-family: var(--font-family-heading);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.bh-card-range {
  color: #444;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

/* --- Detalle: tabla de funciones --- */
.bh-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  cursor: pointer;
  padding: 0;
  margin-bottom: 14px;
  opacity: 0.85;
  transition: opacity 0.15s ease;
}

.bh-back:hover {
  opacity: 1;
}

.bh-detail-head {
  margin-bottom: 18px;
}

.bh-detail-title {
  font-family: var(--font-family-heading);
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--color-theater-text);
  margin: 0;
}

.bh-detail-years {
  color: rgba(255, 255, 255, 0.65);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.bh-table-scroll {
  overflow-x: auto;
}

.bh-table {
  width: 100%;
  border-collapse: collapse;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
}

.bh-table th {
  text-align: left;
  padding: 8px 16px 16px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
}

.bh-table td {
  padding: 14px 16px;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.4px;
  vertical-align: middle;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  white-space: nowrap;
}

.bh-cell-strong {
  text-transform: uppercase;
}

.bh-status {
  font-weight: 800;
  letter-spacing: 0.6px;
}

.bh-status-active {
  color: #4ea1ff;
}

.bh-status-done {
  color: #46c98b;
}

.bh-status-cancelled {
  color: #ff6b6b;
}

.bh-cell-actions {
  text-align: right;
}

.bh-docs-btn {
  display: inline-block;
  padding: 6px 14px;
  border: 1px solid #4ea1ff;
  border-radius: 6px;
  color: #4ea1ff;
  text-decoration: none;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.8px;
  transition: background 0.15s ease, color 0.15s ease;
}

.bh-docs-btn:hover {
  background: #4ea1ff;
  color: #04121f;
}

.bh-empty {
  text-align: center;
  padding: 34px 16px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
}

@media (max-width: 600px) {
  .bh-body {
    padding: 20px 18px 26px;
  }

  .bh-cards {
    gap: 12px;
  }

  .bh-card {
    width: calc(50% - 6px);
  }
}
</style>
